// Client-side pre-flight for `validation.*` rules. Mirrors the
// server's runValidationsForTodo so the user sees inline errors
// instantly instead of waiting for the create round-trip to 400.
//
// Walker errors are non-fatal — the server still runs the
// authoritative gate, so a client-side walker hiccup doesn't
// block writes. Worst case the user sees the server's 400.
//
// Item 7c: prefer the rule's `messageBody` (Array<Segment>) over
// the legacy `messageTemplate` string when present. The walker
// produces a FormattedString; `renderString` collapses it to a
// plain-text line for the existing inline-error string slot.

import { walker, buildTodoContext, evalDisplayThrowing, defaultHostFrame, type HostFrame } from './walker';
import { latestRules } from './rule-cache';
import { renderString } from './renderSegments';
import { emitDisplayFallback } from './display-fallback';
import type { Todo } from '@/infra/types/domain';
import type { Expr } from '@elka/runtime';

export interface ValidationFailure {
  ruleID: string;
  message: string;
}

/**
 * Walk every `validation.*` rule against the candidate todo.
 * Returns the first failing rule's (id, message), or null if
 * everything passes / nothing's loaded yet.
 */
export function runClientValidations(
  todo: Todo,
  host: HostFrame,
): ValidationFailure | null {
  const rules = latestRules();
  if (!rules || rules.size === 0) return null;
  const ctx = buildTodoContext(todo, host);
  for (const [id, rule] of rules) {
    if (!id.startsWith('validation.')) continue;
    const validation = rule.validation;
    if (!validation || !validation.predicate) continue;
    let ok = false;
    try {
      const result = walker.eval(validation.predicate as Expr, ctx);
      if (result.type !== 'AST_TYPE_BOOL') continue;
      ok = result.value;
    } catch {
      // Walker trap — server's authoritative gate will catch
      // this. Skip rather than block the write.
      continue;
    }
    if (!ok) {
      let message: string | undefined;
      // Prefer messageBody: walks the FormattedString Expr and
      // renders to a plain string. Walker traps fall back to
      // messageTemplate, then to the generic message.
      const body = validation.messageBody;
      if (body) {
        try {
          const fs = evalDisplayThrowing(body, ctx);
          if (fs.segments.length > 0) {
            const browserTZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            const rendered = renderString(fs, {
              locale: navigator.language || 'en-US',
              timeZone: browserTZ,
            });
            if (rendered.length > 0) {
              message = rendered;
            } else {
              emitDisplayFallback(id, 'empty', todo.id);
            }
          } else {
            emitDisplayFallback(id, 'empty', todo.id);
          }
        } catch {
          // Fall through to messageTemplate.
          emitDisplayFallback(id, 'walker_trap', todo.id);
        }
      } else {
        emitDisplayFallback(id, 'cold_start', todo.id);
      }
      message ??= validation.messageTemplate ?? 'Validation failed.';
      return { ruleID: id, message };
    }
  }
  return null;
}

/**
 * Evaluate a single validation rule against a todo and return whether
 * the predicate holds. Used by detail/swipe UIs to gate buttons on
 * `rule.validation.todo.can_be_blocked` and similar booleans.
 *
 * Returns `true` when:
 *   - the rule is loaded AND the predicate evaluates to true.
 * Returns `false` when:
 *   - the rule is loaded AND the predicate evaluates to false, OR
 *   - the walker throws (treat unknown as "not blockable" so we
 *     don't surface an action the server would 409 on).
 * Returns `null` when:
 *   - the rule cache is empty (boot, before /sync/pull lands) OR
 *   - the rule id isn't present in the cache.
 *   The caller decides the cold-start policy — most UIs treat null
 *   as "show the action, let the server gate" for parity with the
 *   create-todo flow that lets validations sync in async.
 */
export function evalValidationRule(
  ruleID: string,
  todo: Todo,
  host?: HostFrame,
): boolean | null {
  const rules = latestRules();
  if (!rules || rules.size === 0) return null;
  const rule = rules.get(ruleID);
  if (!rule) return null;
  const validation = rule.validation;
  if (!validation || !validation.predicate) return null;
  const ctx = buildTodoContext(todo, host ?? defaultHostFrame('', '', []));
  try {
    const result = walker.eval(validation.predicate as Expr, ctx);
    if (result.type !== 'AST_TYPE_BOOL') return false;
    return result.value;
  } catch {
    return false;
  }
}
